import { fetchRecommendedItemIfNeeded, getSortedRecommendedItemIdsFromState } from './recommendedItem';
import {
    fetchSavedSearchesIfNeeded,
    fetchSavedSearchItemsIfNeeded,
    getSavedSearchItemIds,
    hasSavedSearchesSelector,
} from './savedSearch';
import { fetchUpcomingItemsIfNeeded, getUpcomingItemIds } from './upcomingItems';
import { getAuthToken, isUserLoggedIn } from './user';
import { LOAD_HOME_DATA_FAIL, LOAD_HOME_DATA_REQUEST, LOAD_HOME_DATA_SUCCESS } from './actions';
import api from '@/redux/api/page';
import { createSelector } from 'reselect';
import { fetchItemCardModelsIfNeeded } from './itemCardModel';
import { fetchSellerUpcomingCatalogsIfNeeded } from './sellerUpcomingCatalogs';
import { fetchUpcomingCatalogsIfNeeded } from './upcomingCatalogs';
import { getAllCatalogs } from './catalog';
import { getDeployment } from './config';
import { getFollowedSellerIds } from './followSeller';
import { GlobalState } from '../rootReducer';
import isEmpty from 'lodash/isEmpty';
import ms from 'ms';
import shuffle from 'lodash/shuffle';

const REDUX_STORE_TIME = ms('5m');

// reducer
const DEFAULT_STATE = {
    acn: {},
    featuredCatalogs: [],
    loaded: 0,
    loading: false,
    trendingItems: [],
    upcomingCatalogs: [],
};

export type State = typeof DEFAULT_STATE;

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    let featuredCatalogs;

    switch (action.type) {
        case LOAD_HOME_DATA_FAIL:
            return {
                ...state,
                loading: false,
            };
        case LOAD_HOME_DATA_REQUEST:
            return {
                ...state,
                loading: true,
            };
        case LOAD_HOME_DATA_SUCCESS:
            // featuredCatalogs
            if (Array.isArray(action.payload.featuredCatalogs)) {
                if (Number.isInteger(action.payload.featuredCatalogs[0])) {
                    // old way, an array of ints
                    featuredCatalogs = [...action.payload.featuredCatalogs];
                } else {
                    // new way, an array of objects
                    featuredCatalogs = action.payload.featuredCatalogs.map((x) => x.catalogId);
                }
            } else {
                // bad, not an array
                featuredCatalogs = [];
            } // TODO: make this reducer pure by moving the shuffle to the selector

            featuredCatalogs = shuffle(featuredCatalogs);
            return {
                ...state,
                acn: { ...action.payload.acn },
                featuredCatalogs,
                loaded: action.meta.actionTime,
                loading: false,
                trendingItems: [...action.payload.trendingItems],
                upcomingCatalogs: [...action.payload.upcomingCatalogs],
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.home;

export const getFeaturedCatalogIds = createSelector([stateSelector, getAllCatalogs], (state, allCatalogs) => {
    return state.featuredCatalogs
        .map((catalogId) => allCatalogs[catalogId])
        .filter((c) => c)
        .filter((catalog) => catalog.catalogStatus !== 'done')
        .map((catalog) => catalog.catalogId)
        .slice(0, 30);
});

export const getUpcomingCatalogIds = createSelector([stateSelector, getAllCatalogs], (state, allCatalogs) => {
    return state.upcomingCatalogs
        .map((catalogId) => allCatalogs[catalogId])
        .filter((c) => c)
        .filter((catalog) => catalog.catalogStatus !== 'done')
        .map((catalog) => catalog.catalogId);
});

const shouldFetchHomeData = createSelector(stateSelector, (state) => {
    const { loaded, loading } = state;
    if (loading) {
        return false;
    }

    if (loaded || isEmpty(state.featuredCatalogs)) {
        const time = Date.now();
        const diff = time - loaded;
        if (diff < REDUX_STORE_TIME) {
            return false;
        }
    }
    return true;
});

/* ACTION CREATORS */
const loadHomeData = (forceUpdate) => async (dispatch: Function, getState: Function) => {
    try {
        const state = getState();
        const deployment = getDeployment(state);
        const authToken = getAuthToken(state);
        dispatch({
            type: LOAD_HOME_DATA_REQUEST,
        });
        const response = await api.fetchHomeData({ authToken, deployment, forceUpdate });
        dispatch({
            meta: { actionTime: Date.now() },
            payload: response.data,
            type: LOAD_HOME_DATA_SUCCESS,
        });
    } catch (error) {
        dispatch({
            error: true,
            payload: error,
            type: LOAD_HOME_DATA_FAIL,
        });
    }
};

export const fetchHomeDataIfNeeded =
    (forceUpdate: boolean = false) =>
    async (dispatch: Function, getState: Function) => {
        if (forceUpdate || shouldFetchHomeData(getState())) {
            return dispatch(loadHomeData(forceUpdate));
        }
        return Promise.resolve();
    };

const MAX_TODAY_AUCTIONS = 20;
export const fetchAllHomeData = () => async (dispatch: Function, getState: Function) => {
    const state = getState();
    const hasSavedSearches = hasSavedSearchesSelector(state);
    const followedSellerIds = getFollowedSellerIds(state);
    return Promise.all([
        dispatch(fetchHomeDataIfNeeded()),
        dispatch(fetchUpcomingItemsIfNeeded()),
        dispatch(fetchUpcomingCatalogsIfNeeded(MAX_TODAY_AUCTIONS, true)),
        dispatch(fetchSavedSearchesIfNeeded()),
        dispatch(fetchSavedSearchItemsIfNeeded(hasSavedSearches)),
        dispatch(fetchRecommendedItemIfNeeded(false)),
        ...followedSellerIds.map((sellerId) => dispatch(fetchSellerUpcomingCatalogsIfNeeded(sellerId))),
    ]).then(() => {
        const newState = getState();
        const isLoggedIn = isUserLoggedIn(newState);
        const promises = [];

        if (isLoggedIn) {
            const upcomingItemIds = getUpcomingItemIds(newState);
            const savedSearchItemIds = getSavedSearchItemIds(newState);
            const recommendedItemIds = getSortedRecommendedItemIdsFromState(newState, 12);
            promises.push(dispatch(fetchItemCardModelsIfNeeded(upcomingItemIds, 'COMING-UP-Carousel')));
            promises.push(dispatch(fetchItemCardModelsIfNeeded(savedSearchItemIds, 'FOLLOWED-SEARCH-Carousel')));
            promises.push(dispatch(fetchItemCardModelsIfNeeded(recommendedItemIds, 'RECOMMENDED-ITEM-Carousel')));
        }

        return Promise.all(promises);
    });
};
